import { createDefaultProjectDocument } from './defaultProjectDocument.js'
import {
  ANIMATION_TYPES,
  CANVAS_SIZE_PRESETS,
  EASING_TYPES,
  LOOP_MODES,
  MAX_ANIMATION_SEGMENT_COUNT,
  MAX_ANIMATION_SEGMENT_WEIGHT,
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXEL_AREA,
  MAX_LAYOUT_OFFSET_COUNT,
  MIN_CANVAS_DIMENSION,
  MODULE_SHAPES,
  NAMED_CANVAS_PRESET_DIMENSIONS,
  PLAYBACK_DIRECTIONS,
  PROJECT_DOCUMENT_VERSION,
  TEXT_ALIGNS,
  TEXT_BASELINES,
  type AnimationSegment,
  type AnimationType,
  type CanvasSizePreset,
  type CustomEasingCurve,
  type Offset2D,
  type ProjectDocumentV2,
  type RendererPreference,
  type RGBColor,
} from './ProjectDocument.js'
import { validateCustomSvgData } from './validateCustomSvg.js'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function recordAt(record: UnknownRecord, key: string): UnknownRecord {
  const value = record[key]
  return isRecord(value) ? value : {}
}

function stringAt(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback
}

function booleanAt(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function numberAt(value: unknown, fallback: number, minimum: number, maximum: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.min(maximum, Math.max(minimum, value))
}

function integerAt(value: unknown, fallback: number, minimum: number, maximum: number): number {
  return Math.round(numberAt(value, fallback, minimum, maximum))
}

function enumAt<const TValue extends string>(
  value: unknown,
  allowed: readonly TValue[],
  fallback: TValue,
): TValue {
  return typeof value === 'string' && allowed.includes(value as TValue)
    ? value as TValue
    : fallback
}

function colorAt(value: unknown, fallback: RGBColor): RGBColor {
  if (!isRecord(value)) {
    return { ...fallback }
  }

  return {
    r: numberAt(value.r, fallback.r, 0, 255),
    g: numberAt(value.g, fallback.g, 0, 255),
    b: numberAt(value.b, fallback.b, 0, 255),
  }
}

function pointAt(value: unknown, fallback: Offset2D, minimum: number, maximum: number): Offset2D {
  if (!isRecord(value)) {
    return { ...fallback }
  }

  return {
    x: numberAt(value.x, fallback.x, minimum, maximum),
    y: numberAt(value.y, fallback.y, minimum, maximum),
  }
}

function offsetsAt(value: unknown): Offset2D[] {
  if (!Array.isArray(value)) {
    return []
  }

  const length = Math.min(value.length, MAX_LAYOUT_OFFSET_COUNT)
  return Array.from(
    { length },
    (_, index) => pointAt(value[index], { x: 0, y: 0 }, -100_000, 100_000),
  )
}

/**
 * Sanitizes the optional segmented timeline (shared contract): entries with an
 * unknown mode or a non-finite/non-positive weight are dropped, survivors are
 * capped to the timeline budget, and a timeline with no valid entries is
 * treated as absent so playback keeps single-mode behavior.
 */
function segmentsAt(value: unknown): AnimationSegment[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }

  const segments: AnimationSegment[] = []
  const length = Math.min(value.length, MAX_ANIMATION_SEGMENT_COUNT)
  for (let index = 0; index < length; index += 1) {
    const entry: unknown = value[index]
    if (!isRecord(entry)) {
      continue
    }
    if (
      typeof entry.type !== 'string'
      || !ANIMATION_TYPES.includes(entry.type as AnimationType)
    ) {
      continue
    }
    if (
      typeof entry.weight !== 'number'
      || !Number.isFinite(entry.weight)
      || entry.weight <= 0
    ) {
      continue
    }
    segments.push({
      type: entry.type as AnimationType,
      weight: Math.min(entry.weight, MAX_ANIMATION_SEGMENT_WEIGHT),
    })
  }

  return segments.length === 0 ? undefined : segments
}

function easingControlPointAt(value: unknown, fallback: Offset2D): Offset2D {
  if (!isRecord(value)) {
    return { ...fallback }
  }

  return {
    x: numberAt(value.x, fallback.x, 0, 1),
    y: numberAt(value.y, fallback.y, -2, 3),
  }
}

function easingCurveAt(value: unknown, fallback: CustomEasingCurve): CustomEasingCurve {
  if (!isRecord(value)) {
    return {
      p1: { ...fallback.p1 },
      p2: { ...fallback.p2 },
    }
  }

  return {
    p1: easingControlPointAt(value.p1, fallback.p1),
    p2: easingControlPointAt(value.p2, fallback.p2),
  }
}

function rendererAt(value: unknown, fallback: RendererPreference): RendererPreference {
  if (value === 'canvas2d') {
    return 'canvas2d'
  }

  if (value === 'webgl2' || value === 'webgl' || value === 'pixi') {
    return 'webgl2'
  }

  return fallback
}

function canvasPresetAt(
  value: unknown,
  fallback: CanvasSizePreset,
  width: number,
  height: number,
): CanvasSizePreset {
  const candidate = typeof value === 'string'
    && CANVAS_SIZE_PRESETS.includes(value as CanvasSizePreset)
    ? value as CanvasSizePreset
    : value === undefined
      ? fallback
      : 'custom'

  if (candidate === 'custom') {
    return 'custom'
  }

  const dimensions = NAMED_CANVAS_PRESET_DIMENSIONS[candidate]
  return dimensions.width === width && dimensions.height === height
    ? candidate
    : 'custom'
}

function seedAt(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.trunc(value) >>> 0
}

interface CanvasDimensions {
  width: number
  height: number
}

function canvasDimensionsAt(
  widthValue: unknown,
  heightValue: unknown,
  fallbackWidth: number,
  fallbackHeight: number,
): CanvasDimensions {
  let width = integerAt(
    widthValue,
    fallbackWidth,
    MIN_CANVAS_DIMENSION,
    MAX_CANVAS_DIMENSION,
  )
  let height = integerAt(
    heightValue,
    fallbackHeight,
    MIN_CANVAS_DIMENSION,
    MAX_CANVAS_DIMENSION,
  )
  if (width * height > MAX_CANVAS_PIXEL_AREA) {
    const scale = Math.sqrt(MAX_CANVAS_PIXEL_AREA / (width * height))
    width = Math.max(MIN_CANVAS_DIMENSION, Math.floor(width * scale))
    height = Math.max(MIN_CANVAS_DIMENSION, Math.floor(height * scale))

    if (width * height > MAX_CANVAS_PIXEL_AREA) {
      height = Math.max(
        MIN_CANVAS_DIMENSION,
        Math.floor(MAX_CANVAS_PIXEL_AREA / width),
      )
    }
  }

  return { width, height }
}

function migrateV2(input: UnknownRecord, defaults: ProjectDocumentV2): ProjectDocumentV2 {
  const text = recordAt(input, 'text')
  const particles = recordAt(input, 'particles')
  const appearance = recordAt(input, 'appearance')
  const animation = recordAt(input, 'animation')
  const layout = recordAt(input, 'layout')
  const canvas = recordAt(input, 'canvas')
  const canvasDimensions = canvasDimensionsAt(
    canvas.width,
    canvas.height,
    defaults.canvas.width,
    defaults.canvas.height,
  )
  const canvasPreset = canvasPresetAt(
    canvas.preset,
    defaults.canvas.preset,
    canvasDimensions.width,
    canvasDimensions.height,
  )
  const animationSegments = segmentsAt(animation.segments)

  return {
    version: PROJECT_DOCUMENT_VERSION,
    seed: seedAt(input.seed, defaults.seed),
    text: {
      content: stringAt(text.content, defaults.text.content),
      activeFont: stringAt(text.activeFont, defaults.text.activeFont),
      fontSize: numberAt(text.fontSize, defaults.text.fontSize, 24, 300),
      lineHeight: numberAt(text.lineHeight, defaults.text.lineHeight, 1, 4),
      wordSpacing: numberAt(text.wordSpacing, defaults.text.wordSpacing, 0, 2),
      textAlign: enumAt(text.textAlign, TEXT_ALIGNS, defaults.text.textAlign),
      baseline: enumAt(text.baseline, TEXT_BASELINES, defaults.text.baseline),
    },
    particles: {
      shape: enumAt(particles.shape, MODULE_SHAPES, defaults.particles.shape),
      density: numberAt(particles.density, defaults.particles.density, 1, 25),
      scale: numberAt(particles.scale, defaults.particles.scale, 0.2, 3),
      customSvg: validateCustomSvgData(particles.customSvg),
    },
    appearance: {
      color: colorAt(appearance.color, defaults.appearance.color),
      chaosColor: colorAt(appearance.chaosColor, defaults.appearance.chaosColor),
      fadeInChaos: booleanAt(appearance.fadeInChaos, defaults.appearance.fadeInChaos),
      gridMode: booleanAt(appearance.gridMode, defaults.appearance.gridMode),
      colorTransitionStart: numberAt(appearance.colorTransitionStart, defaults.appearance.colorTransitionStart, 0, 1),
      colorTransitionEnd: numberAt(appearance.colorTransitionEnd, defaults.appearance.colorTransitionEnd, 0, 1),
      showBackground: booleanAt(appearance.showBackground, defaults.appearance.showBackground),
      backgroundColor: colorAt(appearance.backgroundColor, defaults.appearance.backgroundColor),
      showBorder: booleanAt(appearance.showBorder, defaults.appearance.showBorder),
      borderIntensity: numberAt(appearance.borderIntensity, defaults.appearance.borderIntensity, 0, 1),
      borderColor: colorAt(appearance.borderColor, defaults.appearance.borderColor),
      borderRadius: numberAt(appearance.borderRadius, defaults.appearance.borderRadius, 0, 0.5),
      borderOnTop: booleanAt(appearance.borderOnTop, defaults.appearance.borderOnTop),
      borderScale: numberAt(appearance.borderScale, defaults.appearance.borderScale, 0.5, 3),
      showGlow: booleanAt(appearance.showGlow, defaults.appearance.showGlow),
      glowIntensity: numberAt(appearance.glowIntensity, defaults.appearance.glowIntensity, 0, 1),
      glowColor: colorAt(appearance.glowColor, defaults.appearance.glowColor),
      glowSoftness: numberAt(appearance.glowSoftness, defaults.appearance.glowSoftness, 0, 60),
    },
    animation: {
      type: enumAt(animation.type, ANIMATION_TYPES, defaults.animation.type),
      ...(animationSegments === undefined ? {} : { segments: animationSegments }),
      speed: numberAt(animation.speed, defaults.animation.speed, 0.1, 100),
      easingType: enumAt(animation.easingType, EASING_TYPES, defaults.animation.easingType),
      easingAmount: numberAt(animation.easingAmount, defaults.animation.easingAmount, 0, 1),
      customEasingCurve: easingCurveAt(animation.customEasingCurve, defaults.animation.customEasingCurve),
      playbackDirection: enumAt(animation.playbackDirection, PLAYBACK_DIRECTIONS, defaults.animation.playbackDirection),
      holdAtOrder: numberAt(animation.holdAtOrder, defaults.animation.holdAtOrder, 0, 30),
      holdAtChaos: numberAt(animation.holdAtChaos, defaults.animation.holdAtChaos, 0, 30),
      loopMode: enumAt(animation.loopMode, LOOP_MODES, defaults.animation.loopMode),
      wiggleEnabled: booleanAt(animation.wiggleEnabled, defaults.animation.wiggleEnabled),
      wiggleSpeed: numberAt(animation.wiggleSpeed, defaults.animation.wiggleSpeed, 0, 20),
      wiggleForce: numberAt(animation.wiggleForce, defaults.animation.wiggleForce, 0, 20),
      wiggleDampening: numberAt(animation.wiggleDampening, defaults.animation.wiggleDampening, 0.1, 1),
      rotationEnabled: booleanAt(animation.rotationEnabled, defaults.animation.rotationEnabled),
      rotationSpeed: numberAt(animation.rotationSpeed, defaults.animation.rotationSpeed, 0, 10),
      motionBlur: numberAt(animation.motionBlur, defaults.animation.motionBlur, 0, 1),
    },
    layout: {
      wordOffsets: offsetsAt(layout.wordOffsets),
      letterOffsets: offsetsAt(layout.letterOffsets),
      dotOffsets: offsetsAt(layout.dotOffsets),
      letterMode: booleanAt(layout.letterMode, defaults.layout.letterMode),
      dotMode: booleanAt(layout.dotMode, defaults.layout.dotMode),
    },
    canvas: {
      width: canvasDimensions.width,
      height: canvasDimensions.height,
      preset: canvasPreset,
      preferredRenderer: rendererAt(canvas.preferredRenderer, defaults.canvas.preferredRenderer),
    },
  }
}

function migrateV1(input: UnknownRecord, defaults: ProjectDocumentV2): ProjectDocumentV2 {
  const fontSettings = recordAt(input, 'fontSettings')
  const canvasSize = recordAt(input, 'canvasSize')

  return migrateV2({
    version: PROJECT_DOCUMENT_VERSION,
    seed: input.seed,
    text: {
      content: input.text,
      activeFont: input.activeFont,
      fontSize: fontSettings.fontSize,
      lineHeight: fontSettings.lineHeight,
      wordSpacing: fontSettings.wordSpacing,
      textAlign: fontSettings.textAlign,
      baseline: fontSettings.baseline,
    },
    particles: {
      shape: input.shape,
      density: input.density,
      scale: input.particleScale,
      customSvg: input.customSvgData,
    },
    appearance: {
      color: input.color,
      chaosColor: input.chaosColor,
      fadeInChaos: input.fadeInChaos,
      gridMode: input.gridMode,
      colorTransitionStart: input.colorTransitionStart,
      colorTransitionEnd: input.colorTransitionEnd,
      showBackground: input.showBackground,
      backgroundColor: input.backgroundColor,
      showBorder: input.showBorder,
      borderIntensity: input.borderIntensity,
      borderColor: input.borderColor,
      borderRadius: input.borderRadius,
      borderOnTop: input.borderOnTop,
      borderScale: input.borderScale,
      showGlow: input.showGlow,
      glowIntensity: input.glowIntensity,
      glowColor: input.glowColor,
      glowSoftness: input.glowSoftness,
    },
    animation: {
      type: input.animationType,
      segments: input.segments,
      speed: input.animationSpeed,
      easingType: input.easingType,
      easingAmount: input.easingAmount,
      customEasingCurve: input.customEasingCurve,
      playbackDirection: input.playbackDirection,
      holdAtOrder: input.holdAtOrder,
      holdAtChaos: input.holdAtChaos,
      loopMode: input.loopMode,
      wiggleEnabled: input.wiggleEnabled,
      wiggleSpeed: input.wiggleSpeed,
      wiggleForce: input.wiggleForce,
      wiggleDampening: input.wiggleDampening,
      rotationEnabled: input.rotationEnabled,
      rotationSpeed: input.rotationSpeed,
      motionBlur: input.motionBlur,
    },
    layout: {
      wordOffsets: input.wordOffsets,
      letterOffsets: input.letterOffsets,
      dotOffsets: input.dotOffsets,
      letterMode: input.letterMode,
      dotMode: input.dotMode,
    },
    canvas: {
      width: canvasSize.width,
      height: canvasSize.height,
      preset: canvasSize.preset,
      preferredRenderer: input.rendererType,
    },
  }, defaults)
}

/**
 * Converts persisted settings to the current project document without mutating
 * or retaining references to the source. Unknown versions fail closed to a
 * fresh default document so corrupt storage cannot poison the editor session.
 */
export function migrateProjectDocument(input: unknown): ProjectDocumentV2 {
  const defaults = createDefaultProjectDocument()
  if (!isRecord(input)) {
    return defaults
  }

  if (input.version === PROJECT_DOCUMENT_VERSION) {
    return migrateV2(input, defaults)
  }

  if (input.version === 1) {
    return migrateV1(input, defaults)
  }

  return defaults
}
